/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import ApiService, { ApiResponse } from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import { dropTask, lastValue } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import AlertsService from 'cio/services/alerts';
import EnvironmentService from 'cio/services/environment';
import AnalyticsService from 'cio/services/analytics';
import InAppCredentialsService from 'cio/services/in-app-credentials';
import { CredentialErrors, GistAuthenticationToken, CredentialData } from 'cio/utils/in-app/credentials';
import { TrackedEvents } from 'cio/utils/in-app/events';
import RouterService from '@ember/routing/router-service';

function isUnauthorized(err: ApiResponse) {
  if (!Array.isArray(err.errors)) {
    return false;
  }

  return err.errors[0].detail.includes('401 UNAUTHORIZED');
}

const AUTH_TOKEN_INVALID = 'Service Key is invalid';
const ACCOUNT_SID_OR_AUTH_TOKEN_INVALID = 'Organization ID and/or Service Key is invalid';

interface InAppSettingsArgs {
  showForm?: boolean;
  messageId?: string;
  onMessageIdChange?: (messageId: string) => unknown;
}

export default class InAppSettings extends Component<InAppSettingsArgs> {
  @service api!: ApiService;
  @service router!: RouterService;
  @service apiErrors!: ApiErrorsService;
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service environment!: EnvironmentService;
  @service inAppCredentials!: InAppCredentialsService;

  @tracked isIntegrationEnabled = false;
  /* eslint-disable-next-line ember/no-tracked-properties-from-args */
  @tracked isConfiguringManually = this.args.showForm;
  @tracked isSettingsViewedTracked = false;
  @tracked organizationId = '';
  @tracked errors!: CredentialErrors;
  @tracked messageId = this.args.messageId ?? '';

  get loadCredential() {
    return this.inAppCredentials.loadCredential;
  }
  get loadGistAuthenticationToken() {
    return this.inAppCredentials.loadGistAuthenticationToken;
  }
  @lastValue('loadCredential') credentialData: CredentialData | null = null;
  @lastValue('loadGistAuthenticationToken') gistAuthToken: GistAuthenticationToken | null = null;

  constructor(owner: unknown, args: InAppSettingsArgs) {
    super(owner, args);
    this.loadIntegrationData.perform();
  }

  get isTokenValid() {
    let loadTask = this.inAppCredentials.loadGistAuthenticationToken;
    return !loadTask.isRunning && this.gistAuthToken?.payload != null;
  }

  loadIntegrationData = dropTask(async () => {
    let { credential, isIntegrationEnabled } = await this.inAppCredentials.loadCredential.perform();
    this.isIntegrationEnabled = isIntegrationEnabled;
    if (credential != null) {
      this.organizationId = credential?.username ?? '';
    }
    await this.trackSettingsViewed();
    await this.inAppCredentials.loadGistAuthenticationToken.perform();
  });

  private async trackSettingsViewed() {
    if (!this.isSettingsViewedTracked) {
      this.isSettingsViewedTracked = true;
      this.track(TrackedEvents.SetttingsViewed);
    }
  }

  private track(event: TrackedEvents) {
    this.analytics.track('in_app', event, { enabled: this.isIntegrationEnabled });
  }

  @action
  onInAppEnabled() {
    this.reloadCredential();
  }

  createOrUpdateCredential = dropTask(async (username: string, password: string) => {
    this.track(TrackedEvents.EnablingStarted);
    let success = await this.tryToCreateOrUpdateCredential(username, password);
    if (!success) {
      return;
    }
    await this.reloadCredential();
    this.track(TrackedEvents.EnablingCompleted);
  });

  async tryToCreateOrUpdateCredential(username: string, password: string) {
    try {
      await this.api.put('credentials/in_app', {
        data: { credential: { username, password } },
        ignoreErrors: true,
      });
      return true;
    } catch (err) {
      if (isUnauthorized(err)) {
        if (this.credentialData?.credential) {
          this.errors = { password: [AUTH_TOKEN_INVALID] };
        } else {
          this.errors = {
            username: [ACCOUNT_SID_OR_AUTH_TOKEN_INVALID],
            password: [ACCOUNT_SID_OR_AUTH_TOKEN_INVALID],
          };
        }
      } else {
        this.apiErrors.report(err);
      }
    }
    return false;
  }

  disableCredential = dropTask(async () => {
    await this.api.delete('credentials/in_app');
    this.track(TrackedEvents.Disabled);
    this.isConfiguringManually = false;
    await this.reloadCredential();
  });

  private async reloadCredential() {
    await this.environment.reload.perform();
    await this.loadIntegrationData.perform();
    if (this.args.showForm === true) {
      this.router.transitionTo('in-app-messages');
    }
  }

  @action
  configureManually() {
    this.isConfiguringManually = true;
  }

  @action
  backToLandingPage() {
    this.isConfiguringManually = false;
  }
}
